import React, { useEffect, useMemo, useState } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

function SortableThumb({ id, file, index }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "relative flex flex-col gap-1 rounded-md border border-border bg-muted/30 p-1.5",
        isDragging && "z-10 opacity-90 shadow-md ring-2 ring-primary/40"
      )}
    >
      <div className="relative aspect-square w-full overflow-hidden rounded bg-muted">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={file.name || `Photo ${index + 1}`}
            className="h-full w-full object-cover"
            draggable={false}
          />
        ) : null}
        <button
          type="button"
          className="absolute left-1 top-1 inline-flex h-7 w-7 cursor-grab items-center justify-center rounded-md bg-background/90 border border-border shadow-sm active:cursor-grabbing"
          aria-label={`Drag to reorder photo ${index + 1}`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
        <span className="absolute bottom-1 right-1 rounded bg-background/90 px-1.5 py-0.5 text-[10px] font-medium tabular-nums">
          {index + 1}
        </span>
      </div>
      <p className="truncate px-0.5 text-[10px] text-muted-foreground">
        {file.name || `Photo ${index + 1}`}
      </p>
    </div>
  );
}

/**
 * Modal to drag-reorder pending image Files before post submit.
 */
export default function PhotoOrderEditor({
  open,
  onOpenChange,
  files = [],
  onConfirm,
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!open) return;
    const list = Array.isArray(files) ? files.filter(Boolean) : [];
    setItems(
      list.map((file, i) => ({
        id: `photo-${i}-${file.name}-${file.size}-${file.lastModified}`,
        file,
      }))
    );
  }, [open, files]);

  const ids = useMemo(() => items.map((i) => i.id), [items]);

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setItems((prev) => {
      const oldIndex = prev.findIndex((i) => i.id === active.id);
      const newIndex = prev.findIndex((i) => i.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const handleConfirm = () => {
    onConfirm?.(items.map((i) => i.file));
    onOpenChange?.(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Arrange photos</DialogTitle>
          <DialogDescription>
            Drag to set the order they appear in the post carousel.
          </DialogDescription>
        </DialogHeader>
        {items.length ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
          >
            <SortableContext items={ids} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {items.map((item, index) => (
                  <SortableThumb
                    key={item.id}
                    id={item.id}
                    file={item.file}
                    index={index}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <p className="text-sm text-muted-foreground py-6 text-center">
            No photos to arrange.
          </p>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange?.(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={items.length < 2}
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
